import { CalculatePricePromotion } from '@teko/cart/v2';
import { GiftFilled } from '@teko/ds-icon';
import {
  Body,
  Caption,
  Image,
  LazyImage,
  Modal,
  Shape,
  styled,
} from '@teko/ds-mobile';
import defaultPromotion from 'assets/default-promotion.svg';
import DOMPurify from 'dompurify';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useEffect, useRef, useState } from 'react';

interface DefaultPromotionsProps {
  promotions: CalculatePricePromotion['promotions'];
}

export const DefaultPromotions: React.FC<DefaultPromotionsProps> = (props) => {
  const { promotions } = props;
  const { epicOmni1643 } = useTenantConfig();
  const [previewImg, setPreviewImg] = useState<string>('');
  const promotionListRef = useRef<HTMLUListElement>(null);

  // Intercept clicks on <img> tags rendered inside dangerouslySetInnerHTML
  useEffect(() => {
    const ul = promotionListRef.current;
    if (!ul) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        const src = (target as HTMLImageElement).src;
        if (!!src) setPreviewImg(src);
      }
    };

    ul.addEventListener('click', handleClick);
    return () => ul.removeEventListener('click', handleClick);
  }, []);

  return (
    <>
      <Shape
        padding={['s8', 's0']}
        backgroundColor="white"
        marginTop={epicOmni1643 ? 's12' : 's0'}
      >
        <RelativeWrapper>
          {epicOmni1643 ? (
            <StyledLabel>
              <GiftFilled size={20} color="white" />
              <Caption level={1} weight="medium" color="white">
                {t('Bạn sẽ nhận được')}
              </Caption>
            </StyledLabel>
          ) : (
            <Shape paddingLeft="s8" style={{ marginBottom: -15 }}>
              <LazyImage src={defaultPromotion} width="140px" height="24px" />
            </Shape>
          )}
          <PromotionsWrapper epicOmni1643={epicOmni1643}>
            <StyledUl ref={promotionListRef}>
              {promotions.map((promotion, index) => {
                // tier promotions will have no title
                return !!promotion.title ? (
                  <li key={`${index}${promotion.promotionId}`}>
                    <Body weight="regular">
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            DOMPurify?.sanitize?.(promotion.title) ||
                            promotion.title,
                        }}
                        style={{ whiteSpace: 'pre-line' }}
                      />
                      <Body weight="regular" color="pending700">
                        {promotion.highlightedDescriptions?.[0]}
                      </Body>
                    </Body>
                  </li>
                ) : null;
              })}
            </StyledUl>
          </PromotionsWrapper>
        </RelativeWrapper>
      </Shape>

      {previewImg && (
        <Modal
          visible={!!previewImg}
          onClose={() => setPreviewImg('')}
          width="max-content"
          disableEscClose={false}
          showCloseIcon
          footer={null}
        >
          <Image
            src={previewImg}
            width="100%"
            style={{ maxWidth: '80vw', maxHeight: '80vh' }}
          />
        </Modal>
      )}
    </>
  );
};

const RelativeWrapper = styled.div`
  position: relative;
`;

const PromotionsWrapper = styled(Shape)<{ epicOmni1643?: boolean }>(
  ({ epicOmni1643, theme }) => `
  padding: 16px 8px;
  padding-top: 24px;
  background: ${
    !epicOmni1643
      ? 'linear-gradient(180deg,rgba(250, 225, 76, 0.1) 4.81%,rgba(250, 225, 76, 0) 100%)'
      : theme.colors.secondary100
  };
  border:  ${!epicOmni1643 ? '1px solid #ffd591' : 'none'};
  border-radius: 8px;
`,
);

const StyledUl = styled.ul`
  padding-left: 0; // remove default padding of ul
  margin: 0;
  margin-left: 2rem;
  img {
    height: 20px;
    width: 20px;
    border-radius: 4px;
    border: 1px solid #0000000f;
    object-fit: contain;
    cursor: zoom-in;
  }
`;

const StyledLabel = styled.div`
  position: absolute;
  top: -12px;
  left: 8px;
  z-index: 1;
  width: max-content;
  height: 24px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.25rem;
  padding: 2px 10px 2px 10px;
  background: ${({ theme }) => theme.colors.primary};
`;
